<template>
  <v-dialog v-model="showDialog" :persistent="true" :no-click-animation="!isEditing" :hide-overlay="!isEditing"
            scrollable :transition="isEditing ? 'scale-transition' : 'scroll-x-reverse-transition'"
            max-width="35%" width="35%"
            content-class="envelope-details-container">
      <v-card>
        <v-card-title class="headline">
          <v-layout row>
            <v-flex class="text-xs-left">
              <span>{{ isEditing ? labels.newEnvelopeHeader : labels.envelopeDetailsHeader }}</span>
            </v-flex>
            <v-flex class="text-xs-right">
              <a v-on:click="closeModal"> <span class="icon-close icon-enabled"></span> </a>
            </v-flex>
          </v-layout>
        </v-card-title>
        <v-card-text class="envelope-details-card-text">
            <v-form ref="newEnvelopeForm" v-if="isEditing" v-show="!isLoading" v-model="formIsValid" @keyup.native.enter="formIsValid && validateForm()" onSubmit="return false;" lazy-validation>
                <v-layout row>
                  <v-flex xs12>
                    <CompanySelector id="buyerCompanySelector" ref="buyerCompanySelector" :countryFlag="buyerCountryFlag" :selectedCountry="newEnvelope.buyer.selectedCountry" :companyTitle="labels.companySelectorTitle" :countries="allowedCountries" @validateForm="validateForm" @selectedCountryChanged="buyerCountryChanged" />
                  </v-flex>
                </v-layout>
                <v-layout row>
                  <v-flex xs12>
                    <transition name="scale">
                      <div v-if="!!envelopeErrorMessage" class="message-error">{{ envelopeErrorMessage }}</div>
                    </transition>
                  </v-flex>
                </v-layout>
                <v-layout row mt-5>
                  <v-flex xs12>
                    <span class="bloctitle">{{ labels.envelopeAmountBlocTitle }}</span>
                    <div class="divider">
                      <v-divider></v-divider>
                    </div>
                  </v-flex>
                </v-layout>
                <v-layout row mt-3>
                  <v-flex xs12>
                    <CurrencyInputField v-model="newEnvelope.amount" @input="validateForm" :label="labels.currencyInputLabel" :placeholder="labels.currencyInputPlaceholder" :currencies="allowedCurrencies" :currency="newEnvelope.currency" @selectedCurrencyChanged="selectedCurrencyChanged" backgroundColor="#ffffff" />
                  </v-flex>
                </v-layout>
            </v-form>
            <div v-if="!isEditing">
              <v-layout row>
                <v-flex xs12>
                  <span :class="envelope.status.toLowerCase()" class="envelopeStatus">
                    <p class="centerTextDetail">{{ envelope.status}}</p>
                  </span>
                </v-flex>
              </v-layout>
              <v-layout row mt-3>
                <v-flex xs12>
                  <span>{{ statusDescription }}</span>
                </v-flex>
              </v-layout>
              <v-layout row mt-5>
                <v-flex xs12>
                  <span class="bloctitle">{{ labels.companySelectorTitle }}</span>
                  <div class="divider">
                    <v-divider></v-divider>
                  </div>
                </v-flex>
              </v-layout>
              <v-layout row mt-3>
                <v-flex xs12>
                  <SelectedCompanyInfos :company="envelope.buyer"/>
                </v-flex>
              </v-layout>
              <v-layout row mt-5>
                <v-flex xs12>
                  <span class="bloctitle">{{ labels.envelopeAmountBlocTitle }}</span>
                  <div class="divider">
                    <v-divider></v-divider>
                  </div>
                </v-flex>
              </v-layout>
              <v-layout row mt-3>
                <v-flex xs4>
                  <v-label>{{ labels.envelopeRequestedAmountLabel }} :</v-label>
                </v-flex>
                <v-flex xs8>
                  <v-label>{{ $formatNumber(envelope.requestedAmount) }} {{ envelope.currencyCode }}</v-label>
                </v-flex>
              </v-layout>
              <v-layout row mt-3>
                <v-flex xs4>
                  <v-label>{{ labels.envelopeAgreedLimitLabel }} :</v-label>
                </v-flex>
                <v-flex xs8>
                  <v-label>{{ $formatNumber(envelope.limit) }} {{ envelope.currencyCode }}</v-label>
                </v-flex>
              </v-layout>
              <v-layout row mt-3>
                <v-flex xs4>
                  <v-label>{{ labels.envelopeTotalAvailableLabel }} :</v-label>
                </v-flex>
                <v-flex xs8>
                  <v-label>{{ $formatNumber(envelope.remaining) }} {{ envelope.currencyCode }}</v-label>
                </v-flex>
              </v-layout>
            </div>
            <div class="processing text-xs-center" v-if="isLoading">
              <v-layout row>
                <v-flex xs12>
                  <v-progress-circular :size="100" indeterminate color="primary"></v-progress-circular>
                </v-flex>
              </v-layout>
              <v-layout row mt-3>
                <v-flex xs12>
                  <span>{{ labels.envelopeProcessingLabel }}</span>
                </v-flex>
              </v-layout>
            </div>
        </v-card-text>
        <v-spacer></v-spacer>
        <v-card-actions>
          <v-layout row>
            <v-flex xs12>
              <v-btn v-if="isEditing" :disabled="!canRequestEnvelope || isLoading" @click="requestEnvelope" color="primary" class="fullsize">{{ labels.submitEnvelopeRequestButtonLabel }}</v-btn>
            </v-flex>
          </v-layout>
        </v-card-actions>
      </v-card>
  </v-dialog>
</template>

<script>
import CompanySelector from '@/components/cover/newcover/company/CompanySelector'
import CurrencyInputField from '@/components/common/CurrencyInputField'
import SelectedCompanyInfos from '@/components/cover/newcover/company/SelectedCompany'
import envelopeApi from '@/services/EnvelopeApi'
import { BusinessException } from '@/services/ErrorHandler'
import { mapState } from 'vuex'
export default {
  props: {
    value: Boolean, // value here is the v-model specified at the parent level
    envelope: Object
  },
  components: {
    CompanySelector,
    CurrencyInputField,
    SelectedCompanyInfos
  },
  data () {
    return {
      labels: {
        newEnvelopeHeader: this.$t('ICE.RequestICE.NEW_ENVELOPE_TITLE'),
        envelopeDetailsHeader: this.$t('ICE.RequestICE.ENVELOPE_DETAILS_TITLE'),
        companySelectorTitle: this.$t('ICE.RequestICE.BUYER_SUB_TITLE'),
        envelopeAmountBlocTitle: this.$t('ICE.RequestICE.ENVELOPE_AMOUNT_SUB_TITLE'),
        currencyInputLabel: this.$t('ICE.RequestICE.AMOUNT_FIELD_NAME'),
        currencyInputPlaceholder: this.$t('ICE.RequestICE.AMOUNT_PLACEHOLDER'),
        submitEnvelopeRequestButtonLabel: this.$t('ICE.RequestICE.SUBMIT_REQUEST_BTN'),
        envelopeProcessingLabel: this.$t('ICE.RequestICE.ENVELOPE_PROCESSING_DESC'),
        envelopeRequestedAmountLabel: this.$t('ICE.RequestICE.REQUESTED_AMOUNT_FIELD_NAME'),
        envelopeAgreedLimitLabel: this.$t('ICE.RequestICE.AGREED_LIMIT_FIELD_NAME'),
        envelopeTotalAvailableLabel: this.$t('ICE.RequestICE.TOTAL_AVAILABLE_FIELD_NAME'),
        envelopeStatusReasons: new Map([
          ['cancelled', this.$t('ICE.RequestICE.CANCELLED_ENVELOPE_STATUS_REASON_DESC')],
          ['assessing', this.$t('ICE.RequestICE.ASSESSING_ENVELOPE_STATUS_DESC')],
          ['active', ''],
          ['failed', ''],
          ['refused', '']
        ])
      },
      isLoading: false,
      formIsValid: false,
      envelopeErrorMessage: undefined,
      canRequestEnvelope: false,
      newEnvelope: {
        amount: 0,
        currency: 'EUR',
        buyer: {
          selectedCountry: 'FR',
          name: ''
        }
      }
    }
  },
  mounted () {
    this.initNewEnvelope()
  },
  methods: {
    initNewEnvelope () {
      this.envelopeErrorMessage = undefined
      this.newEnvelope = {
        amount: null,
        currency: this.defaultCurrency.currency,
        buyer: {
          selectedCountry: this.userInfos.country,
          name: ''
        }
      }
      if (this.$refs.buyerCompanySelector) {
        this.$refs.buyerCompanySelector.setSearchMode()
      }
    },
    validateForm () {
      if (!!this.$refs.newEnvelopeForm && !!this.$refs.buyerCompanySelector) {
        let buyerSelectionIsValid = this.$refs.buyerCompanySelector.validate()
        let amountIsValid = this.newEnvelope.amount > 0
        this.canRequestEnvelope = buyerSelectionIsValid && amountIsValid

        let selectedBuyerId = this.$refs.buyerCompanySelector.getCompanyId()
        if (selectedBuyerId !== this.newEnvelope.buyerId) {
          this.envelopeErrorMessage = undefined
        }
      } else {
        this.canRequestEnvelope = false
      }
      return this.canRequestEnvelope
    },
    closeModal () {
      this.showDialog = false
      this.initNewEnvelope()
    },
    buyerCountryChanged (country) {
      this.newEnvelope.buyer.selectedCountry = country.code
    },
    selectedCurrencyChanged (value) {
      this.newEnvelope.currency = value.currency
    },
    async requestEnvelope () {
      let isValid = this.validateForm()
      if (isValid) {
        this.isLoading = true
        let envelopeRequest = {
          buyerId: this.$refs.buyerCompanySelector.getCompanyId(),
          currencyCode: this.newEnvelope.currency,
          amount: this.newEnvelope.amount
        }
        try {
          let envelope = await envelopeApi.requestEnvelope(envelopeRequest)
          this.$emit('envelopeRequested', envelope)
        } catch (error) {
          if (error instanceof BusinessException) {
            this.envelopeErrorMessage = error.message
          } else {
            this.$store.dispatch('global/setSnackbar', {
              show: true,
              color: 'error',
              text: `An error occured. ${error.message}`,
              timeout: 6000
            })
          }
        } finally {
          this.isLoading = false
        }
      }
    }
  },
  computed: {
    ...mapState('userdetails', ['userInfos']),
    ...mapState('siteconfig', ['config']),
    ...mapState('countries', ['countriesWithFlag', 'currencyFlags']),
    showDialog: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    },
    buyerCountryFlag: function () {
      let result = this.countriesWithFlag.find(f => f.countryCode.toUpperCase() === this.newEnvelope.buyer.selectedCountry.toUpperCase())
      return result ? result.countryFlag : ''
    },
    allowedCountries: function () {
      if (!this.userInfos || !this.countriesWithFlag || !this.userInfos.allowedCountriesForCoverage || !this.config.newCoverAllowedCountries) {
        return []
      }
      let siteConfigAllowedCountries = this.config.newCoverAllowedCountries.toUpperCase().split(',')
      let userAllowedCountries = this.userInfos.allowedCountriesForCoverage ? this.userInfos.allowedCountriesForCoverage.filter(uc => siteConfigAllowedCountries.some(sc => sc.toUpperCase() === uc.toUpperCase())) : []

      // add at least the customer's country
      if (!userAllowedCountries.some(c => c.toUpperCase() === this.userInfos.country.toUpperCase())) {
        userAllowedCountries.push(this.userInfos.country.toUpperCase())
      }

      let countriesWithFlag = this.countriesWithFlag.filter(cf => userAllowedCountries.some(c => c.toUpperCase() === cf.countryCode.toUpperCase()))
      let result = userAllowedCountries.map(c => {
        return {
          code: c,
          flag: countriesWithFlag.find(f => f.countryCode.toUpperCase() === c.toUpperCase()).countryFlag
        }
      })

      return result
    },
    allowedCurrencies: function () {
      let result = this.currencyFlags.filter(cur => this.allowedCountries.some(ctry => cur.countriesCode.includes(ctry.code)))
      if (!result.some(c => c.currency.toUpperCase() === this.defaultCurrency.currency)) {
        result.push(this.defaultCurrency)
      }
      return result
    },
    defaultCurrency: function () {
      let country = this.userInfos.country ? this.userInfos.country : 'FR'
      return this.currencyFlags.find(f => f.countriesCode.includes(country.toUpperCase()))
    },
    isEditing: function () {
      return !this.envelope || !this.envelope.id
    },
    statusDescription: function () {
      if (!!this.envelope && !!this.envelope.status && this.labels.envelopeStatusReasons.has(this.envelope.status.toLowerCase())) {
        return this.labels.envelopeStatusReasons.get(this.envelope.status.toLowerCase())
      } else {
        return ''
      }
    }
  }
}
</script>

<style scoped>

.v-dialog__content {
  z-index: 10001 !important;
}

>>> .envelope-details-container {
  min-width:500px;
  height:100%;
  min-height:100%;
  position:absolute;
  right:0;
  margin: 0;
}
.envelope-details-container .envelope-details-card-text {
  height: 100%;
}
.bloctitle {
  font-size: 15px;
}

.processing {
  display:block;
   width:100%;
   height: 100%;
   margin-top: 50%;
}
.envelopeStatus {
  padding-top: 2px;
}
</style>
